
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { getSubscriptionPlanByTier } from "./types";
import { getCurrentSubscription } from "./subscriptionQueries";

// Get current month's lead imports for the user
export const getCurrentMonthLeadImports = async (): Promise<number> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return 0;

  try {
    const currentMonth = new Date().toISOString().slice(0, 7); // Format: YYYY-MM
    
    // Use a standard query instead of RPC
    const { data, error } = await supabase
      .from('lead_imports')
      .select('count')
      .eq('user_id', session.user.id)
      .eq('month_year', currentMonth)
      .maybeSingle();

    if (error) {
      console.error("Error fetching lead imports:", error);
      return 0;
    }

    return data?.count || 0;
  } catch (error) {
    console.error("Error in getCurrentMonthLeadImports:", error);
    return 0;
  }
};

// Check if user has quota available for importing leads
export const checkUserLeadQuota = async (): Promise<boolean> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return false;

  try {
    // Call database function to check quota
    const { data, error } = await supabase.rpc(
      'user_has_lead_quota',
      { user_uuid: session.user.id }
    );
    
    if (error) {
      console.error("Error checking lead quota:", error);
      return false;
    }
    
    if (!data) {
      toast({
        title: "Import limit reached",
        description: "You've reached your monthly import limit. Please upgrade your subscription for more imports.",
        variant: "destructive",
      });
    }
    
    return !!data;
  } catch (error) {
    console.error("Error in checkUserLeadQuota:", error);
    return false;
  }
};

// Track a new lead import
export const trackLeadImport = async (): Promise<boolean> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return false;

  try {
    // First check quota
    const hasQuota = await checkUserLeadQuota();
    if (!hasQuota) return false;

    const currentMonth = new Date().toISOString().slice(0, 7); // Format: YYYY-MM
    
    // Use standard supabase query instead of RPC
    // First, check if an import record exists for the current month
    const { data: existingImport, error: fetchError } = await supabase
      .from('lead_imports')
      .select('id, count')
      .eq('user_id', session.user.id)
      .eq('month_year', currentMonth)
      .maybeSingle();
      
    if (fetchError && fetchError.code !== 'PGRST116') { // Not found error code
      console.error("Error checking existing lead import:", fetchError);
      return false;
    }

    let success = false;

    if (existingImport) {
      // Update existing record
      const { error: updateError } = await supabase
        .from('lead_imports')
        .update({ count: existingImport.count + 1 })
        .eq('id', existingImport.id);
        
      success = !updateError;
      if (updateError) console.error("Error updating lead import:", updateError);
    } else {
      // Create new record
      const { error: insertError } = await supabase
        .from('lead_imports')
        .insert([{
          user_id: session.user.id,
          month_year: currentMonth,
          count: 1
        }]);
        
      success = !insertError;
      if (insertError) console.error("Error inserting lead import:", insertError);
    }

    return success;
  } catch (error) {
    console.error("Error in trackLeadImport:", error);
    return false;
  }
};
